export type GetdemoEventName = 'status' | 'flow' | 'chat' | 'availability';

export interface GetdemoStatusEvent {
  connected: boolean;
}

export interface GetdemoFlowEvent {
  executionId: string;
  status: string;
}

/**
 * O chat abriu, fechou, ou não havia agente para abrir.
 *
 * Existe para o cliente poder controlar o BOTÃO dele no modo manual: sem `unavailable` ele não
 * tem como saber que o visitante não é elegível, e deixaria um botão que não faz nada na tela.
 */
export interface GetdemoChatEvent {
  state: 'opened' | 'closed' | 'unavailable';
}

/**
 * Existe agente disponível para ESTE visitante, NESTA página?
 *
 * É a resposta que o cliente precisa ANTES de desenhar o botão dele, e por isso é um evento e
 * não uma pergunta de resposta única: num ERP o `Getdemo.identify` chega depois do login, às
 * vezes segundos depois do load, e é ele que decide a audiência. Uma função que respondesse "no
 * carregamento" daria `false` para o visitante que, meio segundo depois, PASSA a ter acesso — e o
 * cliente teria escondido o botão de quem tem direito a ele. O evento dispara de novo a cada
 * mudança (identify tardio, navegação SPA que entra ou sai das regras de página), então o botão
 * acompanha.
 *
 * `available` é "dá para abrir um chat aqui": há agente elegível E ele tem interface de chat (um
 * agente só-API não conta, porque não há o que abrir).
 */
export interface GetdemoAvailabilityEvent {
  available: boolean;
}

type Listener = (payload: any) => void;

/**
 * Eventos cujo ÚLTIMO valor é reentregue a quem assina depois de ele já ter acontecido.
 *
 * Sem isso há uma corrida impossível de vencer do lado do cliente: `status` e `availability`
 * descrevem um estado, não um acontecimento, e quem assina 200ms tarde ficaria esperando para
 * sempre pela próxima mudança. `chat` e `flow` ficam de fora de propósito — são acontecimentos,
 * e reentregar um "abriu" antigo faria o cliente reagir a algo que já passou.
 */
const REPLAY_LAST: ReadonlySet<GetdemoEventName> = new Set<GetdemoEventName>([
  'status',
  'availability'
]);

export class GetdemoEventEmitter {
  private listeners = new Map<GetdemoEventName, Set<Listener>>();
  private last = new Map<GetdemoEventName, any>();

  on(event: GetdemoEventName, cb: Listener): void {
    if (typeof cb !== 'function') return;
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(cb);
    if (REPLAY_LAST.has(event) && this.last.has(event)) {
      try {
        cb(this.last.get(event));
      } catch {
        /* listener error */
      }
    }
  }

  emit(event: GetdemoEventName, payload: any): void {
    if (REPLAY_LAST.has(event)) this.last.set(event, payload);
    const set = this.listeners.get(event);
    if (!set) return;
    for (const cb of set) {
      try {
        cb(payload);
      } catch {
        /* listener error */
      }
    }
  }
}

export const getdemoEmitter = new GetdemoEventEmitter();

const isEventName = (value: unknown): value is GetdemoEventName =>
  value === 'status' || value === 'flow' || value === 'chat' || value === 'availability';

// Attaches Getdemo.on to the public surface, draining any ['on', [event, cb]] entries the
// snippet stub queued before this loader ran. Idempotent — safe to call on every poll tick
// (the SDK may replace window.Getdemo during its own init, discarding a previous attach).
export const ensureOnSurface = (emitter: GetdemoEventEmitter): void => {
  try {
    const w = window as any;
    const g = (w.Getdemo = w.Getdemo || { q: [] });
    if (Array.isArray(g.q)) {
      const remaining: any[] = [];
      for (const entry of g.q) {
        if (Array.isArray(entry) && entry[0] === 'on') {
          const args = entry[1] || [];
          if (isEventName(args[0])) {
            emitter.on(args[0], args[1]);
            continue;
          }
        }
        remaining.push(entry);
      }
      g.q = remaining;
    }
    if (typeof g.on === 'function' && g.on.__gdEmitterBound) return;
    const bound = (event: unknown, cb: Listener) => {
      if (isEventName(event)) emitter.on(event, cb);
    };
    (bound as any).__gdEmitterBound = true;
    g.on = bound;
  } catch {
    /* window not available */
  }
};
